<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
// NOTICE: KAPTION-INTEGRATION
import { useAssistantsList } from 'dashboard/composables/useAssistantsList';
// NOTICE: KAPTION-INTEGRATION — smart-assignment.MANUAL.4 / smart-assignment.MANUAL.7
// The command bar is not where most agents live; the Resolve menu is the control they
// already open to hand a conversation over, so the same action belongs here too.
import KaptionSmartAssignmentAPI from 'dashboard/api/kaptionSmartAssignment';
import { smartAssignmentAlertMessage } from 'dashboard/helper/kaption/smartAssignment';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import { emitter } from 'shared/helpers/mitt';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
} from 'dashboard/helper/commandbar/events';

import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

const currentChat = computed(() => getters.getSelectedChat.value);
// NOTICE: KAPTION-INTEGRATION — smart-assignment.MANUAL.4 / smart-assignment.MANUAL.7
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const smartAssignmentEnabled = computed(() =>
  isKaptionFlagEnabled('KAPTION_SMART_ASSIGNMENT')
);
const isSmartAssigning = ref(false);

const runSmartAssignment = async () => {
  if (isSmartAssigning.value) return;
  isSmartAssigning.value = true;
  try {
    const { data } = await KaptionSmartAssignmentAPI.resolve(
      currentChat.value.id
    );
    useAlert(smartAssignmentAlertMessage(data, t));
  } catch (error) {
    useAlert(t('CONVERSATION.SMART_ASSIGNMENT.ERROR'));
  } finally {
    isSmartAssigning.value = false;
  }
};

// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
const { canUseFeature } = useFeaturePermissions();
const canSendTranscript = computed(() =>
  canUseFeature('send_transcript_export')
);
// feature-permissions.CONVERSATION.2 - mute_conversation
const canMute = computed(() => canUseFeature('mute_conversation'));
// feature-permissions.CONVERSATION.8 - snooze_conversation
const canSnooze = computed(() => canUseFeature('snooze_conversation'));
// feature-permissions.CONVERSATION.10 - mark_pending
const canMarkPending = computed(() => canUseFeature('mark_pending'));
// feature-permissions.CONVERSATION.11 - block_contact
const canBlockContact = computed(() => canUseFeature('block_contact'));

// NOTICE: KAPTION-INTEGRATION - Teams and assistants for dropdown
const teams = useMapGetter('teams/getTeams');
const { assignableAssistants } = useAssistantsList(false);
const hasTeams = computed(() => teams.value.length > 0);
const hasAssistants = computed(() => assignableAssistants.value.length > 0);

// Mute/Unmute and Block/Unblock state
const isMuted = computed(() => !!currentChat.value?.muted);
const contactId = computed(() => currentChat.value?.meta?.sender?.id);
const currentContact = computed(() =>
  contactId.value ? store.getters['contacts/getContact'](contactId.value) : {}
);
const isContactBlocked = computed(() => !!currentContact.value?.blocked);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

// NOTICE: KAPTION-INTEGRATION - Open ninja-keys modal for assignment/priority
const openNinjaKeys = parent => {
  closeDropdown();
  const ninja = document.querySelector('ninja-keys');
  if (ninja) {
    ninja.open({ parent });
  }
};

// Mute/Unmute conversation
const openMuteModal = () => {
  closeDropdown();
  const ninja = document.querySelector('ninja-keys');
  if (ninja) {
    ninja.open({ parent: 'mute_conversation' });
  }
};

const unmuteConversation = () => {
  closeDropdown();
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONVERSATION.RESOLVE_DROPDOWN.UNMUTE_SUCCESS'));
};

// Send transcript (emitter bridge to EmailTranscriptModal in MoreActions)
const sendTranscript = () => {
  closeDropdown();
  emitter.emit(CMD_SEND_TRANSCRIPT);
};

// Block/Unblock contact
const toggleContactBlock = async () => {
  closeDropdown();
  const newBlocked = !isContactBlocked.value;
  try {
    await store.dispatch('contacts/update', {
      id: contactId.value,
      blocked: newBlocked,
    });
    useAlert(
      newBlocked
        ? t('CONVERSATION.RESOLVE_DROPDOWN.BLOCK_SUCCESS')
        : t('CONVERSATION.RESOLVE_DROPDOWN.UNBLOCK_SUCCESS')
    );
  } catch {
    useAlert(
      newBlocked
        ? t('CONVERSATION.RESOLVE_DROPDOWN.BLOCK_ERROR')
        : t('CONVERSATION.RESOLVE_DROPDOWN.UNBLOCK_ERROR')
    );
  }
};

const toggleStatus = (status, snoozedUntil) => {
  closeDropdown();
  isLoading.value = true;
  store
    .dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status,
      snoozedUntil,
    })
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
      isLoading.value = false;
    });
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      await toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      await toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div class="relative flex items-center justify-end resolve-actions">
    <!-- NOTICE: KAPTION-INTEGRATION - rounded-full for pill-shaped button group -->
    <div
      class="wp-resolve-action rounded-full shadow outline-1 outline flex-shrink-0"
      :class="!showOpenButton ? 'outline-n-container' : 'outline-transparent'"
    >
      <Button
        v-if="isOpen"
        :label="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
        size="sm"
        color="slate"
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdResolveConversation"
      />
      <Button
        v-else-if="isResolved"
        :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        size="sm"
        color="slate"
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else-if="showOpenButton"
        :label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        size="sm"
        color="slate"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-if="showAdditionalActions"
        ref="arrowDownButtonRef"
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="sm"
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        color="slate"
        trailing-icon
        @click="openDropdown"
      />
    </div>
    <!-- NOTICE: KAPTION-INTEGRATION - rounded-3xl for rounder dropdown -->
    <div
      v-if="showActionsDropdown"
      v-on-clickaway="closeDropdown"
      class="border rounded-3xl shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
    >
      <WootDropdownMenu class="mb-0">
        <!-- Section 1: Assignment -->
        <WootDropdownItem>
          <Button
            :label="t('CONVERSATION.HEADER.ASSIGN_TO')"
            ghost
            slate
            sm
            start
            icon="i-lucide-user-plus"
            class="w-full"
            @click="() => openNinjaKeys('assign_an_agent')"
          />
        </WootDropdownItem>
        <!-- NOTICE: KAPTION-INTEGRATION — smart-assignment.MANUAL.4 -->
        <WootDropdownItem v-if="smartAssignmentEnabled">
          <Button
            :label="t('COMMAND_BAR.COMMANDS.SMART_ASSIGNMENT')"
            ghost
            slate
            sm
            start
            icon="i-lucide-wand-sparkles"
            class="w-full"
            :is-loading="isSmartAssigning"
            @click="runSmartAssignment"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="hasTeams">
          <Button
            :label="t('CONVERSATION.HEADER.ASSIGN_TEAM')"
            ghost
            slate
            sm
            start
            icon="i-lucide-users"
            class="w-full"
            @click="() => openNinjaKeys('assign_a_team')"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="hasAssistants">
          <Button
            :label="t('CONVERSATION.HEADER.ASSIGN_ASSISTANT')"
            ghost
            slate
            sm
            start
            icon="i-lucide-bot"
            class="w-full"
            @click="() => openNinjaKeys('assign_an_assistant')"
          />
        </WootDropdownItem>

        <hr class="my-1 border-n-weak" />

        <!-- Section 2: Manage -->
        <WootDropdownItem>
          <Button
            :label="t('CONVERSATION.HEADER.SET_PRIORITY')"
            ghost
            slate
            sm
            start
            icon="i-lucide-signal"
            class="w-full"
            @click="() => openNinjaKeys('assign_priority')"
          />
        </WootDropdownItem>
        <!-- feature-permissions.CONVERSATION.8 -->
        <WootDropdownItem v-if="!isPending && canSnooze">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')"
            ghost
            slate
            sm
            start
            icon="i-lucide-alarm-clock-minus"
            class="w-full"
            @click="() => openSnoozeModal()"
          />
        </WootDropdownItem>
        <!-- feature-permissions.CONVERSATION.10 -->
        <WootDropdownItem v-if="!isPending && canMarkPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')"
            ghost
            slate
            sm
            start
            icon="i-lucide-circle-dot-dashed"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.PENDING)"
          />
        </WootDropdownItem>

        <hr class="my-1 border-n-weak" />

        <!-- Section 3: More actions -->
        <!-- feature-permissions.CONVERSATION.2 -->
        <WootDropdownItem v-if="!isMuted && canMute">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MUTE')"
            ghost
            slate
            sm
            start
            icon="i-lucide-volume-off"
            class="w-full"
            @click="openMuteModal"
          />
        </WootDropdownItem>
        <!-- feature-permissions.CONVERSATION.2 -->
        <WootDropdownItem v-if="isMuted && canMute">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.UNMUTE')"
            ghost
            slate
            sm
            start
            icon="i-lucide-volume-1"
            class="w-full"
            @click="unmuteConversation"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="canSendTranscript">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.SEND_TRANSCRIPT')"
            ghost
            slate
            sm
            start
            icon="i-lucide-share"
            class="w-full"
            @click="sendTranscript"
          />
        </WootDropdownItem>
        <!-- feature-permissions.CONVERSATION.11 -->
        <WootDropdownItem v-if="!isContactBlocked && canBlockContact">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.BLOCK_CONTACT')"
            ghost
            slate
            sm
            start
            icon="i-lucide-ban"
            class="w-full"
            @click="toggleContactBlock"
          />
        </WootDropdownItem>
        <!-- feature-permissions.CONVERSATION.11 -->
        <WootDropdownItem v-if="isContactBlocked && canBlockContact">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.UNBLOCK_CONTACT')"
            ghost
            slate
            sm
            start
            icon="i-lucide-shield-check"
            class="w-full"
            @click="toggleContactBlock"
          />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
  </div>
</template>
